// The leadership next step's plain rules (GRO-238): what a save sends, and what each refusal says.
// The back end applies the same text rule; this copy only lets the editor count and disable Save.

import type { LeadershipNextStep, Opportunity } from '../api/types.ts';
import { formatDate, pacificToday } from './format.ts';

// The Salesforce field is Text 255.
export const MAX_LEADERSHIP_NEXT_STEP_LENGTH = 255;

// What the back end saves: whitespace runs, newlines included, become one space, and the ends are trimmed.
export function normaliseLeadershipText(raw: string): string {
  return raw.split(/\s+/).filter(Boolean).join(' ');
}

// "by someone@cartesia.ai · Sep 27", the saved day read in Pacific. Empty when neither is known.
export function leadershipByline(value: LeadershipNextStep, today: string): string {
  const parts: string[] = [];
  if (value.by) parts.push(`by ${value.by}`);
  if (value.at) parts.push(formatDate(pacificToday(new Date(value.at)), today));
  return parts.join(' · ');
}

// The board's rows with every save since load laid over the payload's value, so the table and the
// drawer both show a step the moment it saves. The payload is only refetched on reload.
export function withLeadershipSaves(
  opportunities: Opportunity[], saves: ReadonlyMap<string, LeadershipNextStep | null>,
): Opportunity[] {
  if (saves.size === 0) return opportunities;
  return opportunities.map(opportunity => (
    saves.has(opportunity.opp_id) ? { ...opportunity, leadership_next_step: saves.get(opportunity.opp_id) ?? null } : opportunity
  ));
}

// One plain sentence per refusal (payload.md, the other responses). Told apart by code, never by text.
export function saveErrorMessage(status: number | null, code: string | null): string {
  if (code === 'writes_off') return "Saving to Salesforce is switched off for this deal, so nothing was saved.";
  if (code === 'salesforce_failed') return "Salesforce didn't accept the change, so nothing was saved. Try again in a minute.";
  if (code === 'text_too_long') return `A leadership next step is at most ${MAX_LEADERSHIP_NEXT_STEP_LENGTH} characters.`;
  if (code === 'not_found') return "This deal isn't on the board any more, so nothing was saved.";
  if (code === 'building') return 'The board is still loading. Try again in a few seconds.';
  if (status === 401) return 'Your session has expired. Reload the page to sign in again.';
  if (status === 403 || code === 'no_access') return 'Only leadership can set a leadership next step.';
  return "Something went wrong, so nothing was saved. Try again in a minute.";
}
